import DayJS from 'dayjs';
import IsSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import RelativeTime from 'dayjs/plugin/relativeTime';

DayJS.extend(IsSameOrBefore);
DayJS.extend(LocalizedFormat);
DayJS.extend(RelativeTime);

const DateService = {
    format: (date: Date | string | number, formatString = 'L LT') => {
        try {
            return DayJS(date).format(formatString);
        } catch {
            return '';
        }
    },

    formatToDate: (date: Date | string) => {
        return DateService.format(date, 'L');
    },

    formatDistanceToNow: (date: Date | string, addSuffix: boolean = true) => {
        try {
            return DayJS(date).fromNow(!addSuffix);
        } catch {
            return '';
        }
    },

    // eg. "5m", "3h", "2d", "4w"
    formatDistanceToNowShort: (date: Date | string) => {
        const now = DayJS();
        const units = [
            { unit: 'minute', suffix: 'm', max: 60 },
            { unit: 'hour', suffix: 'h', max: 24 },
            { unit: 'day', suffix: 'd', max: 7 },
            { unit: 'week', suffix: 'w', max: 5 },
        ] as const;

        for (const { unit, suffix, max } of units) {
            const distance = now.diff(date, unit);

            if (distance < max) {
                return `${Math.max(distance, 1)}${suffix}`;
            }
        }

        return DateService.format(date, now.isSame(date, 'year') ? 'MMM D' : 'll');
    },

    isToday: (date: Date | string) => {
        return DayJS(date).isSame(DayJS(), 'day');
    },

    interval(start: Date | null, end: Date | null, interval: 'day' = 'day') {
        const dates: Date[] = [];

        if (!start || !end) {
            return dates;
        }

        let current = DayJS(start);

        while (current.isSameOrBefore(DayJS(end).endOf(interval))) {
            dates.push(current.toDate());
            current = current.add(1, interval);
        }

        return dates;
    },

    formatNumberOfDays: (days: number) => {
        const years = Math.round(days / 365);
        const months = Math.round(days / 30);
        const weeks = Math.round(days / 7);

        if (years > 1.25) {
            return 'more than a year';
        }

        if (years > 1) {
            return '1 year';
        }

        if (months > 1) {
            return `${months} ${months > 1 ? 'months' : 'month'}`;
        }

        if (weeks > 1) {
            return `${weeks} ${weeks > 1 ? 'weeks' : 'week'}`;
        }

        return `${days} ${days === 1 ? 'day' : 'days'}`;
    },
};

export { DateService };
